import React, { useEffect, useMemo, useState } from "react";
import { Box, Typography } from "@mui/material";
import { useHistory } from "react-router-dom";
import {
  AreaChart,
  Area,
  XAxis,
  Tooltip,
  ResponsiveContainer,
  ReferenceDot,
} from "recharts";
import ApiService from "./../../services/api-service";
import { isOwner } from "./../../constants/function";

const FONT = "var(--font-body)";
const ACCENT = "var(--hud-accent, #FFEE20)";
const TEXT = "var(--hud-text, #E8E8EE)";
const TEXT_MUTED = "var(--hud-text-muted, #9A9AB0)";
const TEXT_DIM = "var(--hud-text-dim, #6A6A80)";
const BORDER = "var(--hud-border, #16161F)";
const SURFACE = "var(--hud-sidebar-bg, #08080C)";
const POS = "#7CD992";
const NEG = "#E26B6B";

const RANGES = [
  { id: "all", label: "ALL" },
  { id: "30d", label: "30D" },
  { id: "7d", label: "7D" },
];

const PAGE_SIZE = 100;
const MAX_PAGES = 20;
const earningTypes = ["royalty", "tip", "subscription", "purchase", "songRequestReceived"];

const isEarning = (tx) => {
  if (!tx) return false;
  if (!earningTypes.includes(tx.type)) return false;
  return tx.toUser?.email ? isOwner(tx.toUser.email) : false;
};

const startOfDay = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  return d;
};

const formatNumber = (v, digits = 3) =>
  v.toLocaleString(undefined, { minimumFractionDigits: digits, maximumFractionDigits: digits });

const buildLastNDays = (n, labelMode) => {
  const days = [];
  const today = startOfDay(new Date());
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(today);
    d.setDate(today.getDate() - i);
    const label =
      labelMode === "date"
        ? `${d.getMonth() + 1}/${d.getDate()}`
        : d.toLocaleDateString(undefined, { weekday: "short" }).charAt(0);
    days.push({
      key: d.toISOString().slice(0, 10),
      label,
      fullLabel: d.toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric" }).toUpperCase(),
      total: 0,
    });
  }
  return days;
};

const monthKey = (date) =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}`;

const buildMonthsBetween = (firstDate, lastDate) => {
  const months = [];
  const cursor = new Date(firstDate.getFullYear(), firstDate.getMonth(), 1);
  const end = new Date(lastDate.getFullYear(), lastDate.getMonth(), 1);
  while (cursor <= end) {
    months.push({
      key: monthKey(cursor),
      label: cursor.toLocaleDateString(undefined, { month: "short" }).toUpperCase(),
      fullLabel: cursor
        .toLocaleDateString(undefined, { month: "short", year: "numeric" })
        .toUpperCase(),
      total: 0,
    });
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return months;
};

const Corner = ({ pos }) => {
  const styles = {
    tl: { top: -1, left: -1, borderTop: `1px solid ${ACCENT}`, borderLeft: `1px solid ${ACCENT}` },
    tr: { top: -1, right: -1, borderTop: `1px solid ${ACCENT}`, borderRight: `1px solid ${ACCENT}` },
    bl: { bottom: -1, left: -1, borderBottom: `1px solid ${ACCENT}`, borderLeft: `1px solid ${ACCENT}` },
    br: { bottom: -1, right: -1, borderBottom: `1px solid ${ACCENT}`, borderRight: `1px solid ${ACCENT}` },
  };
  return (
    <Box
      sx={{
        position: "absolute",
        width: 6,
        height: 6,
        pointerEvents: "none",
        opacity: 0.65,
        ...styles[pos],
      }}
    />
  );
};

const ChartTooltip = ({ active, payload }) => {
  if (!active || !payload?.length) return null;
  const item = payload[0].payload;
  return (
    <Box
      sx={{
        backgroundColor: "rgba(8,8,12,0.92)",
        backdropFilter: "blur(6px)",
        WebkitBackdropFilter: "blur(6px)",
        px: 1,
        py: 0.5,
        borderLeft: `1px solid ${ACCENT}`,
        boxShadow: "0 4px 20px rgba(0,0,0,0.5)",
        minWidth: 88,
      }}
    >
      <Typography
        sx={{
          fontFamily: FONT,
          fontSize: "8px",
          color: TEXT_DIM,
          letterSpacing: "1.6px",
          fontWeight: 500,
        }}
      >
        {item.fullLabel}
      </Typography>
      <Box sx={{ display: "flex", alignItems: "baseline", gap: 0.5, mt: 0.25 }}>
        <Typography
          sx={{
            fontFamily: FONT,
            fontSize: "12px",
            fontWeight: 600,
            color: ACCENT,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "0.2px",
            lineHeight: 1,
          }}
        >
          {formatNumber(item.total)}
        </Typography>
        <Typography sx={{ fontFamily: FONT, fontSize: "7.5px", color: TEXT_DIM, letterSpacing: "1.5px", fontWeight: 600 }}>
          SIKI
        </Typography>
      </Box>
    </Box>
  );
};

// `txFilter` optionally narrows the earnings to a single category (e.g. tips,
// song requests) so the chart can mirror a selected transaction-history tab.
// `label` overrides the header copy to name that category.
const MonetizationChart = ({ showWalletLink = true, height = 88, txFilter = null, label = "Earnings" } = {}) => {
  const history = useHistory();
  const [range, setRange] = useState("all");
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const loadAll = async () => {
      try {
        setLoading(true);
        setError(false);
        const collected = [];
        for (let page = 1; page <= MAX_PAGES; page++) {
          const response = await ApiService.TRANSACTIONS({ pageNo: page, pageSize: PAGE_SIZE });
          if (cancelled) return;
          const batch = response?.result || [];
          collected.push(...batch);
          if (batch.length < PAGE_SIZE) break;
        }
        if (!cancelled) setTransactions(collected);
      } catch (e) {
        if (!cancelled) setError(true);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    loadAll();
    return () => {
      cancelled = true;
    };
  }, []);

  const earnings = useMemo(
    () =>
      transactions
        .filter(isEarning)
        .filter((tx) => (txFilter ? txFilter(tx) : true))
        .map((tx) => {
          const dateStr = tx.createdDate || tx.createdAt;
          const date = dateStr ? new Date(dateStr) : null;
          return { date, amount: Number(tx.amount) || 0 };
        })
        .filter((e) => e.date && !Number.isNaN(e.date.getTime())),
    [transactions, txFilter]
  );

  const { data, total, bucketUnit, periodLabel, delta } = useMemo(() => {
    if (range === "7d" || range === "30d") {
      const n = range === "7d" ? 7 : 30;
      const days = buildLastNDays(n, range === "30d" ? "date" : "weekday");
      const index = Object.fromEntries(days.map((d) => [d.key, d]));
      let sum = 0;

      const today = startOfDay(new Date());
      const startCurrent = new Date(today);
      startCurrent.setDate(today.getDate() - (n - 1));
      const startPrior = new Date(today);
      startPrior.setDate(today.getDate() - (2 * n - 1));
      const endPrior = new Date(today);
      endPrior.setDate(today.getDate() - n);
      endPrior.setHours(23, 59, 59, 999);

      let prior = 0;
      earnings.forEach(({ date, amount }) => {
        const key = date.toISOString().slice(0, 10);
        if (index[key]) {
          index[key].total += amount;
          sum += amount;
        }
        if (date >= startPrior && date <= endPrior) prior += amount;
      });

      let d = null;
      if (prior > 0) d = ((sum - prior) / prior) * 100;
      else if (sum > 0) d = 100;
      else d = 0;

      return {
        data: days,
        total: sum,
        bucketUnit: "day",
        periodLabel: range === "7d" ? "LAST 7 DAYS" : "LAST 30 DAYS",
        delta: d,
      };
    }

    if (!earnings.length) {
      return { data: [], total: 0, bucketUnit: "month", periodLabel: "ALL TIME", delta: null };
    }
    const first = earnings.reduce((min, e) => (e.date < min ? e.date : min), earnings[0].date);
    const last = new Date();
    const months = buildMonthsBetween(first, last);
    const index = Object.fromEntries(months.map((m) => [m.key, m]));
    let sum = 0;
    earnings.forEach(({ date, amount }) => {
      const key = monthKey(date);
      if (index[key]) {
        index[key].total += amount;
        sum += amount;
      }
    });
    const fmt = (d) => d.toLocaleDateString(undefined, { month: "short", year: "2-digit" }).toUpperCase();
    return {
      data: months,
      total: sum,
      bucketUnit: "month",
      periodLabel: `${fmt(first)} → NOW`,
      delta: null,
    };
  }, [earnings, range]);

  const hasData = total > 0;
  const maxTicks = 6;
  const xAxisInterval =
    data.length > maxTicks ? Math.max(0, Math.ceil(data.length / maxTicks) - 1) : 0;
  const last = hasData ? data[data.length - 1] : null;

  return (
    <Box mb={2}>
      {/* Header strip */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
        <Box display="flex" alignItems="center" gap={0.75}>
          <Typography
            sx={{
              fontFamily: FONT,
              fontSize: "9px",
              fontWeight: 700,
              color: TEXT_MUTED,
              letterSpacing: "3px",
              textTransform: "uppercase",
            }}
          >
            {label}
          </Typography>
        </Box>
        {showWalletLink && (
          <Typography
            onClick={() => history.push("/app/Wallet")}
            sx={{
              fontFamily: FONT,
              fontSize: "9px",
              color: ACCENT,
              letterSpacing: "1px",
              cursor: "pointer",
              opacity: 0.6,
              transition: "opacity 0.2s ease",
              "&:hover": { opacity: 1 },
            }}
          >
            View Wallet
          </Typography>
        )}
      </Box>

      {/* Card */}
      <Box
        sx={{
          position: "relative",
          p: 1.5,
          border: `1px solid ${BORDER}`,
          backgroundColor: "rgba(8,8,12,0.55)",
          backgroundImage:
            "repeating-linear-gradient(0deg, transparent 0, transparent 3px, rgba(255, 238, 32,0.014) 3px, rgba(255, 238, 32,0.014) 4px)",
          overflow: "hidden",
          transition: "border-color 0.2s ease",
          "&:hover": { borderColor: "rgba(255, 238, 32,0.2)" },
        }}
      >
        <Corner pos="tl" />
        <Corner pos="tr" />
        <Corner pos="bl" />
        <Corner pos="br" />

        {/* Hero row */}
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={1.25}>
          <Box>
            <Box display="flex" alignItems="baseline" gap={0.75}>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: "26px",
                  fontWeight: 600,
                  color: TEXT,
                  fontVariantNumeric: "tabular-nums",
                  letterSpacing: "-0.02em",
                  lineHeight: 1,
                }}
              >
                {formatNumber(total)}
              </Typography>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: "9px",
                  fontWeight: 700,
                  color: ACCENT,
                  letterSpacing: "2.5px",
                }}
              >
                SIKI
              </Typography>
            </Box>
            <Box display="flex" alignItems="center" gap={0.75} mt={0.5}>
              <Typography
                sx={{
                  fontFamily: FONT,
                  fontSize: "8px",
                  fontWeight: 500,
                  color: TEXT_DIM,
                  letterSpacing: "2px",
                }}
              >
                {periodLabel}
              </Typography>
              {delta !== null && (
                <>
                  <Box sx={{ width: "1px", height: 8, backgroundColor: BORDER }} />
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: "8px",
                      fontWeight: 700,
                      letterSpacing: "1.2px",
                      fontVariantNumeric: "tabular-nums",
                      color: delta >= 0 ? POS : NEG,
                    }}
                  >
                    {delta >= 0 ? "▲" : "▼"} {Math.abs(delta).toFixed(1)}%
                  </Typography>
                </>
              )}
            </Box>
          </Box>
        </Box>

        {/* Chart area */}
        <Box sx={{ position: "relative", width: "100%", height }}>
          {/* hairline reference grid */}
          {!loading && !error && data.length > 0 && [0.25, 0.5, 0.75].map((p) => (
            <Box
              key={p}
              sx={{
                position: "absolute",
                left: 0,
                right: 0,
                top: `${p * 100}%`,
                height: "1px",
                backgroundColor: "rgba(255, 238, 32,0.05)",
                pointerEvents: "none",
                zIndex: 0,
              }}
            />
          ))}

          {loading ? (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontFamily: FONT, fontSize: "10px", color: TEXT_DIM }}>
                Loading…
              </Typography>
            </Box>
          ) : error ? (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontFamily: FONT, fontSize: "10px", color: NEG }}>
                Unable to load earnings
              </Typography>
            </Box>
          ) : !data.length ? (
            <Box sx={{ height: "100%", display: "flex", alignItems: "center", justifyContent: "center" }}>
              <Typography sx={{ fontFamily: FONT, fontSize: "10px", color: TEXT_DIM }}>
                No earnings yet
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={data} margin={{ top: 6, right: 8, bottom: 14, left: 6 }}>
                <defs>
                  <linearGradient id="monGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#FFEE20" stopOpacity={0.45} />
                    <stop offset="60%" stopColor="#FFEE20" stopOpacity={0.1} />
                    <stop offset="100%" stopColor="#FFEE20" stopOpacity={0} />
                  </linearGradient>
                  <filter id="monGlow" x="-30%" y="-30%" width="160%" height="160%">
                    <feGaussianBlur stdDeviation="1.6" result="blur" />
                    <feMerge>
                      <feMergeNode in="blur" />
                      <feMergeNode in="SourceGraphic" />
                    </feMerge>
                  </filter>
                </defs>
                <XAxis
                  dataKey="label"
                  axisLine={false}
                  tickLine={false}
                  interval={xAxisInterval}
                  tick={{
                    fill: TEXT_DIM,
                    fontSize: 9,
                    fontFamily: FONT,
                    letterSpacing: "1px",
                  }}
                  height={14}
                  dy={4}
                />
                <Tooltip
                  cursor={{ stroke: "rgba(255, 238, 32,0.45)", strokeWidth: 1, strokeDasharray: "2 3" }}
                  content={<ChartTooltip />}
                  offset={12}
                />
                <Area
                  type="monotone"
                  dataKey="total"
                  stroke="#FFEE20"
                  strokeWidth={1.25}
                  fill="url(#monGrad)"
                  dot={false}
                  activeDot={{
                    r: 3,
                    fill: SURFACE,
                    stroke: "#FFEE20",
                    strokeWidth: 1.5,
                  }}
                  filter="url(#monGlow)"
                  isAnimationActive={hasData}
                  animationDuration={650}
                  animationEasing="ease-out"
                />
                {last && (
                  <ReferenceDot
                    x={last.label}
                    y={last.total}
                    r={2.25}
                    fill="#FFEE20"
                    stroke="none"
                    isFront
                  />
                )}
              </AreaChart>
            </ResponsiveContainer>
          )}
        </Box>

        {/* Footer */}
        <Box
          display="flex"
          justifyContent="space-between"
          alignItems="center"
          sx={{ borderTop: `1px solid ${BORDER}`, pt: 1, mt: 0.5 }}
        >
          <Box display="flex" alignItems="center" gap={1.25}>
            <Typography
              sx={{
                fontFamily: FONT,
                fontSize: "7.5px",
                fontWeight: 500,
                color: TEXT_DIM,
                letterSpacing: "1.8px",
              }}
            >
              {bucketUnit === "month" ? "MONTHLY" : "DAILY"} · {data.length || 0}{" "}
              <Box component="span" sx={{ color: "rgba(154,154,176,0.4)" }}>PTS</Box>
            </Typography>
            <Typography
              onClick={() => history.push("/HowToEarn")}
              sx={{
                fontFamily: FONT,
                fontSize: "9px",
                color: ACCENT,
                letterSpacing: "1px",
                cursor: "pointer",
                opacity: 0.6,
                transition: "opacity 0.2s ease",
                "&:hover": { opacity: 1 },
              }}
            >
              How to earn
            </Typography>
          </Box>
          <Box display="flex" gap={1.5}>
            {RANGES.map((r) => {
              const active = r.id === range;
              return (
                <Box
                  key={r.id}
                  onClick={() => setRange(r.id)}
                  sx={{
                    cursor: "pointer",
                    position: "relative",
                    pb: "3px",
                    transition: "all 0.15s ease",
                  }}
                >
                  <Typography
                    sx={{
                      fontFamily: FONT,
                      fontSize: "8.5px",
                      fontWeight: active ? 700 : 500,
                      letterSpacing: "2px",
                      color: active ? ACCENT : TEXT_DIM,
                      transition: "color 0.15s ease",
                      "&:hover": { color: active ? ACCENT : TEXT },
                    }}
                  >
                    {r.label}
                  </Typography>
                  {active && (
                    <Box
                      sx={{
                        position: "absolute",
                        left: 0,
                        right: 0,
                        bottom: 0,
                        height: "1px",
                        backgroundColor: ACCENT,
                        boxShadow: "0 0 4px rgba(255, 238, 32,0.6)",
                      }}
                    />
                  )}
                </Box>
              );
            })}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default MonetizationChart;
